import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { HexColorPicker } from "react-colorful";

import {
  Category,
  Classification,
  Product,
  Schema,
} from "../../../../../api";
import {
  SSCARACTERISTICA,
  SSINCLUYE,
  SSMARCA,
} from "../../../../../utils";

import styles from "./EditProductPage.module.scss";
import {
  initialValues,
  validationSchema,
  mapProductToForm,
  getProductPayload,
  createLocalId,
  INITIAL_VARIANT,
} from "./EditProductPage.form";

// Ver la nota en CreateProductPage.js: se sube de 10 a 40 porque no hay
// límite real del lado del servidor, solo esta restricción de UX.
const MAX_IMAGES = 40;

function getProductResponse(result) {
  return result?.product || result?.response || result || null;
}

function getCategoriesResponse(result) {
  const response = result?.categories || result?.response || result || {};

  if (Array.isArray(response.docs)) {
    return response.docs;
  }

  if (Array.isArray(response)) {
    return response;
  }

  return [];
}

function getClassificationsResponse(result) {
  const response =
    result?.classifications || result?.response || result || {};

  if (Array.isArray(response.docs)) {
    return response.docs;
  }

  if (Array.isArray(response)) {
    return response;
  }

  return [];
}

function getSchemasResponse(result) {
  const response = result?.schemas || result?.response || result || {};

  if (Array.isArray(response.docs)) {
    return response.docs;
  }

  if (Array.isArray(response)) {
    return response;
  }

  return [];
}

async function fetchClassificationsBySchema(schemaId) {
  const schemaController = new Schema();
  const classificationController = new Classification();

  const schemasResult = await schemaController.getSchemas(1, 1, {
    search: schemaId,
  });

  const schema = getSchemasResponse(schemasResult).find(
    (item) => item?.id === schemaId
  );

  if (!schema) {
    return [];
  }

  const result = await classificationController.getClassifications(
    1,
    100,
    {
      schema: getCategoryId(schema),
    }
  );

  return getClassificationsResponse(result);
}

function getCategoryId(category) {
  return String(category?._id || category?.id || "");
}

function getCategoryDisplayName(category) {
  const level = Math.max(Number(category?.level || 1) - 1, 0);
  const prefix = level > 0 ? `${"— ".repeat(level)}` : "";

  return `${prefix}${category?.name || ""}`;
}

function getErrorMessage(error) {
  if (!error) {
    return "No fue posible completar la operación.";
  }

  if (typeof error === "string") {
    return error;
  }

  return error.msg || error.message || "No fue posible completar la operación.";
}

function normalizeStringArray(value) {
  if (!value) {
    return [];
  }

  const values = Array.isArray(value) ? value : [value];

  return [
    ...new Set(
      values
        .map((item) => String(item || "").trim())
        .filter(Boolean)
    ),
  ];
}

function getVariantLabel(variant) {
  const attributes = Array.isArray(variant?.attributes)
    ? variant.attributes
    : [];

  if (attributes.length === 0) {
    return "Variante única";
  }

  const formatAttribute = (attribute) => {
    const schemaName =
      attribute?.name ||
      attribute?.schemaName ||
      attribute?.schema?.name ||
      "Atributo";

    const classificationName =
      attribute?.label ||
      attribute?.value ||
      attribute?.classificationName ||
      attribute?.classification?.description ||
      attribute?.classification?.name ||
      "—";

    return `${schemaName}: ${classificationName}`;
  };

  const sizeAttributes = attributes.filter(
    (attribute) =>
      normalizeAttributeKey(attribute?.name) ===
      normalizeAttributeKey("Talla")
  );
  const otherAttributes = attributes.filter(
    (attribute) =>
      normalizeAttributeKey(attribute?.name) !==
      normalizeAttributeKey("Talla")
  );

  const parts = [];

  if (sizeAttributes.length > 0) {
    parts.push(sizeAttributes.map(formatAttribute).join(" · "));
  }

  if (otherAttributes.length > 0) {
    parts.push(otherAttributes.map(formatAttribute).join(" · "));
  }

  return parts.join("  |  ");
}

function getVariantError(errors, index, fieldName) {
  return errors?.variants?.[index]?.[fieldName] || "";
}

function normalizeAttributeKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function isHexColor(value) {
  return /^#[0-9a-f]{6}$/i.test(String(value || "").trim());
}

const PRESET_COLORS = [
  { name: "Negro", hex: "#000000" },
  { name: "Blanco", hex: "#ffffff" },
  { name: "Gris claro", hex: "#b0b0b0" },
  { name: "Gris oscuro", hex: "#4a4a4a" },
  { name: "Beige", hex: "#e8dcc8" },
  { name: "Crema", hex: "#f5eede" },
  { name: "Camel", hex: "#c19a6b" },
  { name: "Café claro", hex: "#8b5e3c" },
  { name: "Café oscuro", hex: "#4b2e1e" },
  { name: "Cognac", hex: "#a15c30" },
  { name: "Nude", hex: "#e3bfa4" },
  { name: "Rojo", hex: "#c62828" },
  { name: "Vino/Burdeos", hex: "#6d1b2f" },
  { name: "Rosado", hex: "#f4a6c0" },
  { name: "Rosa palo", hex: "#dba8a0" },
  { name: "Fucsia", hex: "#d6336c" },
  { name: "Naranja", hex: "#e8720c" },
  { name: "Coral", hex: "#ff6f61" },
  { name: "Amarillo", hex: "#f5c518" },
  { name: "Mostaza", hex: "#c9a227" },
  { name: "Dorado", hex: "#c9a86a" },
  { name: "Verde", hex: "#2e7d32" },
  { name: "Verde militar", hex: "#556b2f" },
  { name: "Verde esmeralda", hex: "#0f6b4c" },
  { name: "Verde menta", hex: "#a8d8c9" },
  { name: "Turquesa", hex: "#1abc9c" },
  { name: "Azul", hex: "#1565c0" },
  { name: "Azul marino", hex: "#1a2a4a" },
  { name: "Azul cielo", hex: "#8ecae6" },
  { name: "Morado", hex: "#6a3d9a" },
  { name: "Lila", hex: "#c9a8e0" },
  { name: "Plateado", hex: "#c0c0c0" },
];

function sanitizeSkuSegment(value, maxLength) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .normalize("NFD")
    .replace(new RegExp("[\\u0300-\\u036f]", "g"), "")
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, maxLength);
}

function getVariantAttributeValueForSku(attributes = [], attributeName) {
  const attribute = (attributes || []).find(
    (item) => normalizeAttributeKey(item?.name) === normalizeAttributeKey(attributeName)
  );

  // Se prefiere el nombre legible (p. ej. "Vino/Burdeos") sobre el valor
  // crudo (el hexadecimal del color), para que el SKU generado sirva como
  // referencia real de la variante en vez de un código sin sentido.
  return attribute?.label || attribute?.value || "";
}

function buildSkuPreview({ categoryCode, brandCode, variant, sequence }) {
  const colorValue = getVariantAttributeValueForSku(
    variant?.attributes,
    "Color"
  );
  const tallaValue = getVariantAttributeValueForSku(
    variant?.attributes,
    "Talla"
  );

  const categorySegment = sanitizeSkuSegment(categoryCode, 2) || "GE";
  const brandSegment = sanitizeSkuSegment(brandCode, 2) || "SM";
  const colorSegment = sanitizeSkuSegment(colorValue, 6) || "UNI";
  const tallaSegment = sanitizeSkuSegment(tallaValue, 4) || "UN";

  const sequenceSegment =
    sequence === null || sequence === undefined
      ? "####"
      : String(sequence).padStart(4, "0");

  return `${categorySegment}-${brandSegment}-${colorSegment}-${tallaSegment}-${sequenceSegment}`;
}

function VariantSkuAutoFiller({
  categoryId,
  brandId,
  categoryCode,
  brandCode,
  variants,
  setFieldValue,
}) {
  const [nextSkuSequence, setNextSkuSequence] = useState(null);

  useEffect(() => {
    if (!categoryId) {
      setNextSkuSequence(null);
      return;
    }

    let cancelled = false;

    async function loadNextSku() {
      try {
        const productController = new Product();

        const result = await productController.getNextSku(
          categoryId,
          brandId
        );

        if (!cancelled) {
          setNextSkuSequence(result?.nextSku?.nextSequence ?? null);
        }
      } catch (errorResponse) {
        console.error("getNextSku error:", errorResponse);

        if (!cancelled) {
          setNextSkuSequence(null);
        }
      }
    }

    loadNextSku();

    return () => {
      cancelled = true;
    };
  }, [categoryId, brandId]);

  useEffect(() => {
    if (nextSkuSequence === null || !categoryCode) {
      return;
    }

    let sequenceOffset = 0;

    variants.forEach((variant, index) => {
      if (variant.sku) {
        return;
      }

      const sku = buildSkuPreview({
        categoryCode,
        brandCode,
        variant,
        sequence: nextSkuSequence + sequenceOffset,
      });

      sequenceOffset += 1;

      setFieldValue(`variants[${index}].sku`, sku, false);
    });
  }, [nextSkuSequence, categoryCode, brandCode, variants, setFieldValue]);

  return null;
}

function VariantAttributesEditor({ attributes, onChange, disabled }) {
  const [draftSize, setDraftSize] = useState("");
  const [isEditingSize, setIsEditingSize] = useState(false);

  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const [draftColor, setDraftColor] = useState("#000000");
  const [draftColorName, setDraftColorName] = useState("");
  const [colorError, setColorError] = useState("");

  const list = Array.isArray(attributes) ? attributes : [];

  const sizeAttribute = list.find(
    (attribute) =>
      normalizeAttributeKey(attribute.name) === normalizeAttributeKey("Talla")
  );
  const colorAttribute = list.find(
    (attribute) =>
      normalizeAttributeKey(attribute.name) === normalizeAttributeKey("Color")
  );
  const otherAttributes = list.filter(
    (attribute) =>
      normalizeAttributeKey(attribute.name) !== normalizeAttributeKey("Talla") &&
      normalizeAttributeKey(attribute.name) !== normalizeAttributeKey("Color")
  );

  function handleSetSize() {
    const value = draftSize.trim();

    if (!value) {
      return;
    }

    onChange([
      ...(colorAttribute ? [colorAttribute] : []),
      ...otherAttributes,
      {
        schema: sizeAttribute?.schema ?? null,
        classification: sizeAttribute?.classification ?? null,
        name: "Talla",
        value,
        label: value,
      },
    ]);

    setDraftSize("");
    setIsEditingSize(false);
  }

  // Antes solo se podía quitar la talla y volver a agregarla desde cero
  // para "cambiarla". Esto habilita editarla en el mismo lugar, igual que
  // ya se puede con el color.
  function handleStartEditSize() {
    setDraftSize(sizeAttribute?.label || sizeAttribute?.value || "");
    setIsEditingSize(true);
  }

  function handleCancelEditSize() {
    setDraftSize("");
    setIsEditingSize(false);
  }

  function handleRemoveSize() {
    onChange([...(colorAttribute ? [colorAttribute] : []), ...otherAttributes]);
    setDraftSize("");
    setIsEditingSize(false);
  }

  function handleOpenColorPicker() {
    setDraftColor(
      colorAttribute && isHexColor(colorAttribute.value)
        ? colorAttribute.value
        : "#000000"
    );
    setDraftColorName(
      colorAttribute && colorAttribute.label !== colorAttribute.value
        ? colorAttribute.label || ""
        : ""
    );
    setColorError("");
    setIsColorPickerOpen(true);
  }

  function handleCloseColorPicker() {
    setColorError("");
    setIsColorPickerOpen(false);
  }

  function handleConfirmColor() {
    const colorValue = draftColor.trim().toLowerCase();
    const colorName = draftColorName.trim();

    if (!isHexColor(colorValue)) {
      setColorError(
        "Selecciona o ingresa un color hexadecimal válido, por ejemplo #B33461."
      );
      return;
    }

    // El nombre es obligatorio: el color sirve como referencia para
    // identificar la variante (en el SKU, en el catálogo, en inventario),
    // y un código hexadecimal por sí solo no le dice nada a nadie.
    if (!colorName) {
      setColorError(
        "Ingresa un nombre para el color, por ejemplo: Vino, Azul marino."
      );
      return;
    }

    onChange([
      ...(sizeAttribute ? [sizeAttribute] : []),
      ...otherAttributes,
      {
        schema: colorAttribute?.schema ?? null,
        classification: colorAttribute?.classification ?? null,
        name: "Color",
        value: colorValue,
        label: colorName,
      },
    ]);

    setColorError("");
    setIsColorPickerOpen(false);
  }

  function handleRemoveColor() {
    onChange([...(sizeAttribute ? [sizeAttribute] : []), ...otherAttributes]);
    setIsColorPickerOpen(false);
  }

  function handleRemove(indexToRemove) {
    onChange([
      ...(sizeAttribute ? [sizeAttribute] : []),
      ...(colorAttribute ? [colorAttribute] : []),
      ...otherAttributes.filter((_, index) => index !== indexToRemove),
    ]);
  }

  return (
    <div className={styles.VariantAttributesEditor}>
      <div className={styles.VariantSizeRow}>
        {sizeAttribute && !isEditingSize ? (
          <span className={styles.VariantAttributeChip}>
            <strong>Talla:</strong>{" "}
            {sizeAttribute.label || sizeAttribute.value}

            <button
              type="button"
              onClick={handleStartEditSize}
              disabled={disabled}
              aria-label={`Editar talla ${
                sizeAttribute.label || sizeAttribute.value
              }`}
            >
              ✎
            </button>

            <button
              type="button"
              onClick={handleRemoveSize}
              disabled={disabled}
              aria-label={`Quitar talla ${sizeAttribute.value}`}
            >
              ×
            </button>
          </span>
        ) : (
          <>
            <input
              type="text"
              placeholder="Talla"
              value={draftSize}
              onChange={(event) => setDraftSize(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleSetSize();
                }

                if (event.key === "Escape" && isEditingSize) {
                  event.preventDefault();
                  handleCancelEditSize();
                }
              }}
              disabled={disabled}
              autoFocus={isEditingSize}
            />

            <button
              type="button"
              onClick={handleSetSize}
              disabled={disabled || !draftSize.trim()}
              aria-label={isEditingSize ? "Guardar talla" : "Agregar talla"}
            >
              {isEditingSize ? "✓" : "+"}
            </button>

            {isEditingSize && (
              <button
                type="button"
                onClick={handleCancelEditSize}
                disabled={disabled}
                aria-label="Cancelar edición de talla"
              >
                ×
              </button>
            )}
          </>
        )}
      </div>

      <div className={styles.VariantColorRow}>
        {colorAttribute ? (
          <span className={styles.VariantAttributeChip}>
            <span
              className={styles.VariantColorSwatch}
              style={{ backgroundColor: colorAttribute.value }}
              aria-hidden="true"
            />
            <strong>Color:</strong>{" "}
            {colorAttribute.label || colorAttribute.value}

            <button
              type="button"
              onClick={handleOpenColorPicker}
              disabled={disabled}
              aria-label={`Editar color ${
                colorAttribute.label || colorAttribute.value
              }`}
            >
              ✎
            </button>

            <button
              type="button"
              onClick={handleRemoveColor}
              disabled={disabled}
              aria-label={`Quitar color ${
                colorAttribute.label || colorAttribute.value
              }`}
            >
              ×
            </button>
          </span>
        ) : (
          <button
            type="button"
            className={styles.AddColorButton}
            onClick={handleOpenColorPicker}
            disabled={disabled}
          >
            <span
              className={styles.VariantColorSwatch}
              style={{ backgroundColor: draftColor }}
              aria-hidden="true"
            />
            Agregar color
          </button>
        )}
      </div>

      {isColorPickerOpen && (
        <div className={styles.InlineColorPickerPanel}>
          <div className={styles.PresetColorGrid}>
            {PRESET_COLORS.map((preset) => (
              <button
                key={preset.hex}
                type="button"
                className={styles.PresetColorSwatch}
                style={{ backgroundColor: preset.hex }}
                title={preset.name}
                aria-label={preset.name}
                onClick={() => {
                  setDraftColor(preset.hex);
                  setDraftColorName(preset.name);
                  setColorError("");
                }}
                disabled={disabled}
              />
            ))}
          </div>

          <div className={styles.ColorPickerMain}>
            <HexColorPicker
              color={draftColor}
              onChange={(color) => {
                setDraftColor(color);
                setColorError("");
              }}
            />
          </div>

          <div className={styles.ColorPickerFields}>
            <label>Código hexadecimal</label>

            <div className={styles.HexInputRow}>
              <span
                className={styles.HexPreview}
                style={{ backgroundColor: draftColor }}
                aria-hidden="true"
              />

              <input
                type="text"
                value={draftColor}
                onChange={(event) => {
                  setDraftColor(event.target.value);
                  setColorError("");
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                  }
                }}
                placeholder="#B33461"
                maxLength={7}
                disabled={disabled}
              />
            </div>

            <label>
              Nombre del color <small>(obligatorio)</small>
            </label>

            <input
              type="text"
              value={draftColorName}
              onChange={(event) => setDraftColorName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                }
              }}
              placeholder="Ej. Vino, Azul marino"
              required
              disabled={disabled}
            />

            {colorError && (
              <span className={styles.FieldError}>{colorError}</span>
            )}

            <div className={styles.ColorPickerActions}>
              <button
                type="button"
                className={styles.CancelColorButton}
                onClick={handleCloseColorPicker}
                disabled={disabled}
              >
                Cancelar
              </button>

              <button
                type="button"
                className={styles.ConfirmColorButton}
                onClick={handleConfirmColor}
                disabled={disabled}
              >
                Guardar color
              </button>
            </div>
          </div>
        </div>
      )}

      {otherAttributes.length > 0 && (
        <div className={styles.VariantAttributeChips}>
          {otherAttributes.map((attribute, index) => (
            <span
              key={`${attribute.name}-${attribute.value}-${index}`}
              className={styles.VariantAttributeChip}
            >
              <strong>{attribute.name || "Atributo"}:</strong>{" "}
              {attribute.label || attribute.value}

              <button
                type="button"
                onClick={() => handleRemove(index)}
                disabled={disabled}
                aria-label={`Quitar ${attribute.name}: ${attribute.value}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export function EditProductPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const localObjectUrlsRef = useRef(new Set());

  const [formValues, setFormValues] = useState(initialValues);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [availableCharacteristics, setAvailableCharacteristics] = useState(
    []
  );
  const [availableIncludes, setAvailableIncludes] = useState([]);
  const [selectedVariantImageIndex, setSelectedVariantImageIndex] =
    useState(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [isLoadingBrands, setIsLoadingBrands] = useState(true);
  const [isLoadingCharacteristics, setIsLoadingCharacteristics] =
    useState(true);
  const [isLoadingIncludes, setIsLoadingIncludes] = useState(true);
  const [serverError, setServerError] = useState("");

  useEffect(() => {
    async function loadData() {
      try {
        setIsLoading(true);
        setIsLoadingCategories(true);
        setIsLoadingBrands(true);
        setIsLoadingCharacteristics(true);
        setIsLoadingIncludes(true);
        setServerError("");

        const productController = new Product();
        const categoryController = new Category();

        const [
          productResult,
          categoriesResult,
          brandsList,
          characteristicsList,
          includesList,
        ] = await Promise.all([
          productController.getProduct(productId),
          categoryController.getCategories(1, 100, { visible: true }),
          fetchClassificationsBySchema(SSMARCA),
          fetchClassificationsBySchema(SSCARACTERISTICA),
          fetchClassificationsBySchema(SSINCLUYE),
        ]);

        const product = getProductResponse(productResult);

        if (!product?._id) {
          throw new Error("No se encontró el producto solicitado.");
        }

        setFormValues(mapProductToForm(product));
        setCategories(getCategoriesResponse(categoriesResult));
        setBrands(brandsList);
        setAvailableCharacteristics(characteristicsList);
        setAvailableIncludes(includesList);
      } catch (errorResponse) {
        console.error("loadData error:", errorResponse);
        setServerError(getErrorMessage(errorResponse));
      } finally {
        setIsLoading(false);
        setIsLoadingCategories(false);
        setIsLoadingBrands(false);
        setIsLoadingCharacteristics(false);
        setIsLoadingIncludes(false);
      }
    }

    if (!productId) {
      setServerError("No se recibió el identificador del producto.");
      setIsLoading(false);
      setIsLoadingCategories(false);
      setIsLoadingBrands(false);
      setIsLoadingCharacteristics(false);
      setIsLoadingIncludes(false);
      return;
    }

    loadData();
  }, [productId]);

  useEffect(() => {
    return () => {
      localObjectUrlsRef.current.forEach((url) => {
        URL.revokeObjectURL(url);
      });

      localObjectUrlsRef.current.clear();
    };
  }, []);

  const availableCategories = useMemo(() => {
    return categories.filter((category) => category.visible !== false);
  }, [categories]);

  const availableBrands = useMemo(() => {
    return brands;
  }, [brands]);

  async function handleSubmit(values, { setSubmitting, setErrors }) {
    try {
      setServerError("");

      const productController = new Product();

      await productController.updateProduct(
        productId,
        getProductPayload(values)
      );

      navigate("/admin/products", {
        state: {
          success: "Producto actualizado correctamente.",
        },
      });
    } catch (errorResponse) {
      console.error("updateProduct error:", errorResponse);

      if (errorResponse?.errors) {
        setErrors(errorResponse.errors);
      }

      setServerError(getErrorMessage(errorResponse));
    } finally {
      setSubmitting(false);
    }
  }

  function handleCancel() {
    navigate("/admin/products");
  }

  if (isLoading) {
    return (
      <section className={styles.EditProductPage}>
        <div className={styles.LoadingState}>Cargando producto...</div>
      </section>
    );
  }

  return (
    <section className={styles.EditProductPage}>
      <Formik
        enableReinitialize
        initialValues={formValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          setFieldValue,
          isSubmitting,
        }) => {
          const variants = Array.isArray(values.variants)
            ? values.variants
            : [];

          const selectedCategoryCode =
            availableCategories.find(
              (category) => getCategoryId(category) === values.category
            )?.id || "";

          const selectedBrandCode =
            availableBrands.find(
              (brand) => getCategoryId(brand) === values.brand
            )?.id || "";

          const selectedVariant =
            selectedVariantImageIndex !== null
              ? variants[selectedVariantImageIndex] || null
              : null;

          const hasValidationErrors = Object.keys(errors).length > 0;

          function updateImages(updatedImages) {
            const normalizedImages = updatedImages.map((image, index) => ({
              ...image,
              order: index + 1,
              isCover:
                updatedImages.length > 0
                  ? image.isCover
                  : false,
            }));

            const hasCover = normalizedImages.some(
              (image) => image.isCover
            );

            setFieldValue(
              "images",
              normalizedImages.map((image, index) => ({
                ...image,
                isCover: hasCover ? image.isCover : index === 0,
              }))
            );
          }

          function handleImageChange(event) {
            const selectedFiles = Array.from(event.target.files || []);

            if (selectedFiles.length === 0) {
              return;
            }

            const invalidFile = selectedFiles.find(
              (file) => !file.type.startsWith("image/")
            );

            if (invalidFile) {
              setServerError(
                "Selecciona únicamente archivos de imagen válidos."
              );
              event.target.value = "";
              return;
            }

            const availableSlots = MAX_IMAGES - values.images.length;

            if (availableSlots <= 0) {
              setServerError(
                `Solo puedes cargar hasta ${MAX_IMAGES} imágenes.`
              );
              event.target.value = "";
              return;
            }

            const filesToAdd = selectedFiles.slice(0, availableSlots);

            const newImages = filesToAdd.map((file) => {
              const url = URL.createObjectURL(file);

              localObjectUrlsRef.current.add(url);

              return {
                localId: createLocalId(),
                _id: null,
                file,
                path: "",
                url,
                name: file.name,
                order: 0,
                isCover: false,
                existing: false,
              };
            });

            updateImages([...values.images, ...newImages]);

            if (selectedFiles.length > availableSlots) {
              setServerError(
                `Solo se agregaron ${availableSlots} imágenes. El máximo es ${MAX_IMAGES}.`
              );
            } else {
              setServerError("");
            }

            event.target.value = "";
          }

          function handleRemoveImage(localId) {
            const selectedImage = values.images.find(
              (image) => image.localId === localId
            );

            if (
              selectedImage &&
              !selectedImage.existing &&
              selectedImage.url
            ) {
              URL.revokeObjectURL(selectedImage.url);
              localObjectUrlsRef.current.delete(selectedImage.url);
            }

            const remainingImages = values.images.filter(
              (image) => image.localId !== localId
            );

            updateImages(remainingImages);

            setFieldValue(
              "variants",
              variants.map((variant) => ({
                ...variant,
                images: normalizeStringArray(variant.images).filter(
                  (imageReference) => imageReference !== localId
                ),
              }))
            );

            setServerError("");
          }

          function handleSetCover(localId) {
            setFieldValue(
              "images",
              values.images.map((image) => ({
                ...image,
                isCover: image.localId === localId,
              }))
            );
          }

          function handleNameChange(event) {
            const name = event.target.value;

            setFieldValue("name", name);

            if (!values.slug) {
              setFieldValue("slug", name);
            }
          }

          function handleOpenVariantImages(variantIndex) {
            setSelectedVariantImageIndex(variantIndex);
          }

          function handleCloseVariantImages() {
            setSelectedVariantImageIndex(null);
          }

          function handleToggleVariantImage(variantIndex, imageLocalId) {
            setFieldValue(
              `variants[${variantIndex}].images`,
              (() => {
                const currentImages = normalizeStringArray(
                  variants[variantIndex]?.images
                );

                return currentImages.includes(imageLocalId)
                  ? currentImages.filter(
                      (imageReference) =>
                        imageReference !== imageLocalId
                    )
                  : [...currentImages, imageLocalId];
              })()
            );
          }

          function handleAddVariant() {
            setFieldValue("variants", [
              ...variants,
              { ...INITIAL_VARIANT, attributes: [], images: [] },
            ]);

            setServerError("");
          }

          function handleRemoveVariant(index) {
            if (variants.length <= 1) {
              setServerError(
                "El producto debe tener al menos una variante."
              );
              return;
            }

            setFieldValue(
              "variants",
              variants.filter((_, variantIndex) => variantIndex !== index)
            );

            if (selectedVariantImageIndex === index) {
              setSelectedVariantImageIndex(null);
            } else if (
              selectedVariantImageIndex !== null &&
              selectedVariantImageIndex > index
            ) {
              setSelectedVariantImageIndex(
                selectedVariantImageIndex - 1
              );
            }
          }

          return (
            <>
              <VariantSkuAutoFiller
                categoryId={values.category}
                brandId={values.brand}
                categoryCode={selectedCategoryCode}
                brandCode={selectedBrandCode}
                variants={variants}
                setFieldValue={setFieldValue}
              />

              <header className={styles.PageHeader}>
                <div className={styles.TitleSection}>
                  <button
                    type="button"
                    className={styles.BackButton}
                    onClick={handleCancel}
                    disabled={isSubmitting}
                    aria-label="Volver a productos"
                  >
                    ←
                  </button>

                  <div>
                    <p className={styles.Breadcrumb}>
                      Productos / Editar producto
                    </p>

                    <h1>Editar producto</h1>

                    <p className={styles.Subtitle}>
                      Actualiza la información comercial, variantes e imágenes
                      del producto.
                    </p>
                  </div>
                </div>

                <div className={styles.HeaderActions}>
                  <button
                    type="button"
                    className={styles.CancelButton}
                    onClick={handleCancel}
                    disabled={isSubmitting}
                  >
                    Cancelar
                  </button>

                  <button
                    type="submit"
                    form="edit-product-form"
                    className={styles.PublishButton}
                    disabled={isSubmitting || isLoadingCategories}
                  >
                    {isSubmitting ? "Guardando..." : "Guardar cambios"}
                  </button>
                </div>
              </header>

              {hasValidationErrors && (
                <div className={styles.ValidationMessage} role="alert">
                  <span className={styles.MessageIcon} aria-hidden="true">
                    !
                  </span>

                  <div>
                    <strong>Revisa los campos requeridos.</strong>

                    <span>
                      Corrige los errores marcados antes de guardar los
                      cambios.
                    </span>
                  </div>
                </div>
              )}

              {serverError && (
                <div className={styles.ServerErrorMessage} role="alert">
                  <span className={styles.MessageIcon} aria-hidden="true">
                    !
                  </span>

                  <span>{serverError}</span>
                </div>
              )}

              <Form id="edit-product-form" className={styles.FormContent}>
                <section className={styles.BasicInformationCard}>
                  <header className={styles.SectionHeader}>
                    <div>
                      <h2>Información básica</h2>
                      <p>Define los datos principales del producto.</p>
                    </div>
                  </header>

                  <div className={styles.FormGrid}>
                    <div className={styles.FormField}>
                      <label htmlFor="name">
                        Nombre del producto <span>*</span>
                      </label>

                      <input
                        id="name"
                        name="name"
                        type="text"
                        value={values.name}
                        onChange={handleNameChange}
                        placeholder="Ej. Bolso Elegance Negro"
                        className={
                          errors.name && touched.name ? styles.error : ""
                        }
                        disabled={isSubmitting}
                      />

                      <ErrorMessage
                        name="name"
                        component="span"
                        className={styles.FieldError}
                      />
                    </div>

                    <div className={styles.FormField}>
                      <label htmlFor="category">
                        Categoría <span>*</span>
                      </label>

                      <Field
                        as="select"
                        id="category"
                        name="category"
                        disabled={isSubmitting || isLoadingCategories}
                      >
                        <option value="">
                          {isLoadingCategories
                            ? "Cargando categorías..."
                            : "Selecciona una categoría"}
                        </option>

                        {availableCategories.map((category) => (
                          <option
                            key={getCategoryId(category)}
                            value={getCategoryId(category)}
                          >
                            {getCategoryDisplayName(category)}
                          </option>
                        ))}
                      </Field>

                      <ErrorMessage
                        name="category"
                        component="span"
                        className={styles.FieldError}
                      />
                    </div>

                    <div className={styles.FormField}>
                      <label htmlFor="brand">Marca</label>

                      <Field
                        as="select"
                        id="brand"
                        name="brand"
                        disabled={isSubmitting || isLoadingBrands}
                      >
                        <option value="">
                          {isLoadingBrands
                            ? "Cargando marcas..."
                            : "Sin marca"}
                        </option>

                        {availableBrands.map((brand) => (
                          <option
                            key={getCategoryId(brand)}
                            value={getCategoryId(brand)}
                          >
                            {brand?.name || ""}
                          </option>
                        ))}
                      </Field>

                      <ErrorMessage
                        name="brand"
                        component="span"
                        className={styles.FieldError}
                      />
                    </div>

                    <div className={styles.FormField}>
                      <label htmlFor="slug">
                        Slug / URL <span>*</span>
                      </label>

                      <Field
                        id="slug"
                        name="slug"
                        type="text"
                        placeholder="Ej. bolso-elegance-negro"
                        disabled={isSubmitting}
                      />

                      <ErrorMessage
                        name="slug"
                        component="span"
                        className={styles.FieldError}
                      />
                    </div>

                    <div className={`${styles.FormField} ${styles.FullWidth}`}>
                      <label htmlFor="description">Descripción</label>

                      <Field
                        as="textarea"
                        id="description"
                        name="description"
                        maxLength="5000"
                        placeholder="Describe el producto, materiales, estilo y características principales..."
                        disabled={isSubmitting}
                      />

                      <div className={styles.TextAreaFooter}>
                        <ErrorMessage
                          name="description"
                          component="span"
                          className={styles.FieldError}
                        />

                        <span>{values.description.length} / 5000</span>
                      </div>
                    </div>

                    <div className={`${styles.FormField} ${styles.FullWidth}`}>
                      <label>Características</label>

                      <p className={styles.FieldHelp}>
                        Costuras, herrajes, materiales y similares. Se
                        administran desde el módulo de Clasificaciones,
                        esquema "Características".
                      </p>

                      {isLoadingCharacteristics ? (
                        <span className={styles.FieldHelp}>
                          Cargando características...
                        </span>
                      ) : availableCharacteristics.length === 0 ? (
                        <div className={styles.EmptyConfiguration}>
                          Todavía no hay características registradas.
                          Créalas desde el módulo de Clasificaciones,
                          bajo el esquema "Características".
                        </div>
                      ) : (
                        <div className={styles.CharacteristicsGrid}>
                          {availableCharacteristics.map((characteristic) => {
                            const characteristicId =
                              getCategoryId(characteristic);
                            const selected = Array.isArray(
                              values.characteristics
                            )
                              ? values.characteristics
                              : [];
                            const isSelected =
                              selected.includes(characteristicId);

                            return (
                              <button
                                key={characteristicId}
                                type="button"
                                className={`${styles.CharacteristicChip} ${
                                  isSelected
                                    ? styles.CharacteristicChipActive
                                    : ""
                                }`}
                                onClick={() =>
                                  setFieldValue(
                                    "characteristics",
                                    isSelected
                                      ? selected.filter(
                                          (id) => id !== characteristicId
                                        )
                                      : [...selected, characteristicId]
                                  )
                                }
                                disabled={isSubmitting}
                              >
                                {isSelected ? "✓ " : ""}
                                {characteristic?.name || ""}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div className={`${styles.FormField} ${styles.FullWidth}`}>
                      <label>Incluye</label>

                      <p className={styles.FieldHelp}>
                        Qué trae la pieza: caja, dust bag, etiquetas,
                        tarjeta, shopping bag, etc. Se administra desde
                        el módulo de Clasificaciones, esquema "Incluye".
                      </p>

                      {isLoadingIncludes ? (
                        <span className={styles.FieldHelp}>Cargando...</span>
                      ) : availableIncludes.length === 0 ? (
                        <div className={styles.EmptyConfiguration}>
                          Todavía no hay elementos de "Incluye"
                          registrados. Créalos desde el módulo de
                          Clasificaciones, bajo el esquema "Incluye".
                        </div>
                      ) : (
                        <div className={styles.CharacteristicsGrid}>
                          {availableIncludes.map((item) => {
                            const itemId = getCategoryId(item);
                            const selected = Array.isArray(
                              values.characteristics
                            )
                              ? values.characteristics
                              : [];
                            const isSelected = selected.includes(itemId);

                            return (
                              <button
                                key={itemId}
                                type="button"
                                className={`${styles.CharacteristicChip} ${
                                  isSelected
                                    ? styles.CharacteristicChipActive
                                    : ""
                                }`}
                                onClick={() =>
                                  setFieldValue(
                                    "characteristics",
                                    isSelected
                                      ? selected.filter((id) => id !== itemId)
                                      : [...selected, itemId]
                                  )
                                }
                                disabled={isSubmitting}
                              >
                                {isSelected ? "✓ " : ""}
                                {item?.name || ""}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </section>

                <section className={styles.VariantsCard}>
                  <header className={styles.SectionHeader}>
                    <div>
                      <h2>Variantes</h2>
                      <p>
                        Actualiza SKU, precios, alerta de stock, estado e
                        imágenes de cada combinación. El stock (columna
                        "Stock") no se edita aquí: se administra desde el
                        módulo de Inventario (Entradas, Salidas, Ajustes).
                      </p>
                    </div>

                    <div className={styles.VariantsHeaderActions}>
                      <span className={styles.VariantsCounter}>
                        {variants.length}{" "}
                        {variants.length === 1 ? "variante" : "variantes"}
                      </span>

                      <button
                        type="button"
                        className={styles.AddVariantButton}
                        onClick={handleAddVariant}
                        disabled={isSubmitting}
                      >
                        + Agregar variante
                      </button>
                    </div>
                  </header>

                  <div className={styles.VariantsContent}>
                    {errors.variants && typeof errors.variants === "string" && (
                      <span className={styles.FieldError}>
                        {errors.variants}
                      </span>
                    )}

                    <div className={styles.VariantsTableContainer}>
                      <table className={styles.VariantsTable}>
                        <thead>
                          <tr>
                            <th>Variante</th>
                            <th>Imágenes</th>
                            <th title="Si lo dejas vacío se genera automático a partir de la categoría, la marca, el color y la talla. Siempre puedes escribir o modificar el tuyo.">
                              SKU
                            </th>
                            <th>Precio (Q)</th>
                            <th>Oferta (Q)</th>
                            <th title="Lo que cuesta la pieza. Se usa para calcular el Valor de inventario en el Resumen -- si se deja vacío, esta variante no suma ahí aunque tenga precio y stock.">
                              Costo (Q)
                            </th>
                            <th title="Se administra desde Inventario, no aquí">
                              Stock
                            </th>
                            <th>Alerta</th>
                            <th>Habilitada</th>
                            <th aria-label="Acciones" />
                          </tr>
                        </thead>

                        <tbody>
                          {variants.map((variant, index) => {
                            const assignedImages = values.images.filter(
                              (image) =>
                                normalizeStringArray(
                                  variant.images
                                ).includes(image.localId)
                            );

                            return (
                              // Antes la key incluía variant.sku: al escribir
                              // en el campo de SKU, la key cambiaba en cada
                              // tecla y React destruía y recreaba toda la
                              // fila (perdiendo el foco del input después de
                              // cada carácter). La key debe depender solo de
                              // la posición de la variante, no de un valor
                              // que el usuario está editando en vivo.
                              <tr key={`variant-${index}`}>
                                <td>
                                  <span className={styles.VariantLabel}>
                                    {getVariantLabel(variant)}
                                  </span>

                                  <VariantAttributesEditor
                                    attributes={variant.attributes}
                                    disabled={isSubmitting}
                                    onChange={(updatedAttributes) =>
                                      setFieldValue(
                                        `variants[${index}].attributes`,
                                        updatedAttributes
                                      )
                                    }
                                  />
                                </td>

                                <td>
                                  <button
                                    type="button"
                                    className={
                                      styles.ManageVariantImagesButton
                                    }
                                    onClick={() =>
                                      handleOpenVariantImages(index)
                                    }
                                    disabled={isSubmitting}
                                  >
                                    {assignedImages.length > 0
                                      ? `${assignedImages.length} ${
                                          assignedImages.length === 1
                                            ? "imagen"
                                            : "imágenes"
                                        }`
                                      : "Asignar imágenes"}
                                  </button>

                                  {assignedImages.length > 0 && (
                                    <div className={styles.VariantImageThumbs}>
                                      {assignedImages
                                        .slice(0, 3)
                                        .map((image) => (
                                          <img
                                            key={image.localId}
                                            src={image.url}
                                            alt=""
                                          />
                                        ))}

                                      {assignedImages.length > 3 && (
                                        <span>
                                          +{assignedImages.length - 3}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </td>

                                <td>
                                  <Field
                                    name={`variants[${index}].sku`}
                                    type="text"
                                    placeholder={buildSkuPreview({
                                      categoryCode: selectedCategoryCode,
                                      brandCode: selectedBrandCode,
                                      variant,
                                    })}
                                    disabled={isSubmitting}
                                  />

                                  {getVariantError(errors, index, "sku") && (
                                    <span className={styles.FieldError}>
                                      {getVariantError(errors, index, "sku")}
                                    </span>
                                  )}
                                </td>

                                <td>
                                  <Field
                                    name={`variants[${index}].price`}
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    disabled={isSubmitting}
                                  />

                                  {getVariantError(errors, index, "price") && (
                                    <span className={styles.FieldError}>
                                      {getVariantError(errors, index, "price")}
                                    </span>
                                  )}
                                </td>

                                <td>
                                  <Field
                                    name={`variants[${index}].offerPrice`}
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    disabled={isSubmitting}
                                  />

                                  {getVariantError(
                                    errors,
                                    index,
                                    "offerPrice"
                                  ) && (
                                    <span className={styles.FieldError}>
                                      {getVariantError(
                                        errors,
                                        index,
                                        "offerPrice"
                                      )}
                                    </span>
                                  )}
                                </td>

                                <td>
                                  <Field
                                    name={`variants[${index}].cost`}
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    disabled={isSubmitting}
                                  />

                                  {getVariantError(errors, index, "cost") && (
                                    <span className={styles.FieldError}>
                                      {getVariantError(errors, index, "cost")}
                                    </span>
                                  )}
                                </td>

                                <td>
                                  <Field
                                    name={`variants[${index}].stock`}
                                    type="number"
                                    min="0"
                                    step="1"
                                    disabled
                                    readOnly
                                    title="Se administra desde Inventario (Entradas, Salidas, Ajustes)."
                                  />

                                  {getVariantError(errors, index, "stock") && (
                                    <span className={styles.FieldError}>
                                      {getVariantError(errors, index, "stock")}
                                    </span>
                                  )}
                                </td>

                                <td>
                                  <Field
                                    name={`variants[${index}].lowStockAlert`}
                                    type="number"
                                    min="0"
                                    step="1"
                                    disabled={isSubmitting}
                                  />

                                  {getVariantError(
                                    errors,
                                    index,
                                    "lowStockAlert"
                                  ) && (
                                    <span className={styles.FieldError}>
                                      {getVariantError(
                                        errors,
                                        index,
                                        "lowStockAlert"
                                      )}
                                    </span>
                                  )}
                                </td>

                                <td>
                                  <label
                                    className={styles.VariantEnabledSwitch}
                                  >
                                    <Field
                                      name={`variants[${index}].enabled`}
                                      type="checkbox"
                                      disabled={isSubmitting}
                                    />

                                    <span />
                                  </label>
                                </td>

                                <td>
                                  <button
                                    type="button"
                                    className={styles.RemoveVariantButton}
                                    onClick={() =>
                                      handleRemoveVariant(index)
                                    }
                                    disabled={
                                      isSubmitting || variants.length <= 1
                                    }
                                    aria-label={`Eliminar ${getVariantLabel(
                                      variant
                                    )}`}
                                  >
                                    ×
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </section>

                <section className={styles.ImagesCard}>
                  <header className={styles.SectionHeader}>
                    <div>
                      <h2>Imágenes del producto</h2>

                      <p>
                        Sube imágenes y asígnalas a una o más variantes desde
                        la tabla de variantes.
                      </p>
                    </div>

                    <span className={styles.ImagesCounter}>
                      {values.images.length} de {MAX_IMAGES} imágenes
                    </span>
                  </header>

                  <div className={styles.ImagesContent}>
                    <label className={styles.UploadArea} htmlFor="images">
                      <span className={styles.UploadIcon}>↥</span>
                      <strong>Selecciona imágenes</strong>
                      <span>
                        Formatos JPG, PNG o WEBP. Máximo {MAX_IMAGES} imágenes.
                      </span>
                    </label>

                    <input
                      id="images"
                      name="images"
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageChange}
                      disabled={
                        isSubmitting || values.images.length >= MAX_IMAGES
                      }
                      hidden
                    />

                    {values.images.length > 0 && (
                      <div className={styles.ImagesGrid}>
                        {values.images.map((image) => (
                          <article
                            key={image.localId}
                            className={
                              image.isCover
                                ? `${styles.ImageCard} ${styles.CoverImageCard}`
                                : styles.ImageCard
                            }
                          >
                            <img src={image.url} alt={image.name} />

                            {image.isCover && (
                              <span className={styles.CoverBadge}>
                                Portada
                              </span>
                            )}

                            <div className={styles.ImageCardActions}>
                              {!image.isCover && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleSetCover(image.localId)
                                  }
                                  disabled={isSubmitting}
                                >
                                  Usar como portada
                                </button>
                              )}

                              <button
                                type="button"
                                className={styles.RemoveImageButton}
                                onClick={() =>
                                  handleRemoveImage(image.localId)
                                }
                                disabled={isSubmitting}
                              >
                                Eliminar
                              </button>
                            </div>
                          </article>
                        ))}
                      </div>
                    )}
                  </div>
                </section>

                <section className={styles.AdditionalInformationCard}>
                  <header className={styles.SectionHeader}>
                    <div>
                      <h2>Información adicional</h2>
                      <p>Opciones de publicación y configuración SEO.</p>
                    </div>
                  </header>

                  <div className={styles.AdditionalContent}>
                    <div className={styles.SwitchesRow}>
                      <div className={styles.VisibilityField}>
                        <span>Visible en tienda</span>

                        <label className={styles.VisibilitySwitch}>
                          <Field
                            name="visible"
                            type="checkbox"
                            disabled={isSubmitting}
                          />

                          <span />
                        </label>

                        <small>
                          El producto podrá mostrarse en el catálogo público.
                        </small>
                      </div>

                      <div className={styles.VisibilityField}>
                        <span>Producto destacado</span>

                        <label className={styles.VisibilitySwitch}>
                          <Field
                            name="featured"
                            type="checkbox"
                            disabled={isSubmitting}
                          />

                          <span />
                        </label>

                        <small>
                          Podrá aparecer en secciones destacadas de la tienda.
                        </small>
                      </div>
                    </div>

                    <section className={styles.SeoSection}>
                      <h3 className={styles.SeoTitle}>SEO</h3>

                      <p className={styles.SeoDescription}>
                        Optimiza cómo se mostrará el producto en buscadores.
                      </p>

                      <div className={styles.SeoGrid}>
                        <div className={styles.FormField}>
                          <label htmlFor="metaTitle">Título SEO</label>

                          <Field
                            id="metaTitle"
                            name="seo.metaTitle"
                            type="text"
                            maxLength="60"
                            disabled={isSubmitting}
                          />

                          <div className={styles.TextAreaFooter}>
                            <ErrorMessage
                              name="seo.metaTitle"
                              component="span"
                              className={styles.FieldError}
                            />

                            <span>{values.seo.metaTitle.length} / 60</span>
                          </div>
                        </div>

                        <div className={styles.FormField}>
                          <label htmlFor="metaDescription">
                            Descripción meta
                          </label>

                          <Field
                            as="textarea"
                            id="metaDescription"
                            name="seo.metaDescription"
                            maxLength="160"
                            disabled={isSubmitting}
                          />

                          <div className={styles.TextAreaFooter}>
                            <ErrorMessage
                              name="seo.metaDescription"
                              component="span"
                              className={styles.FieldError}
                            />

                            <span>
                              {values.seo.metaDescription.length} / 160
                            </span>
                          </div>
                        </div>
                      </div>
                    </section>
                  </div>
                </section>
              </Form>

              {selectedVariantImageIndex !== null && selectedVariant && (
                <div
                  className={styles.VariantImagesModalOverlay}
                  role="presentation"
                  onMouseDown={handleCloseVariantImages}
                >
                  <div
                    className={styles.VariantImagesModal}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="variant-images-modal-title"
                    onMouseDown={(event) => event.stopPropagation()}
                  >
                    <header className={styles.VariantImagesModalHeader}>
                      <div>
                        <h2 id="variant-images-modal-title">
                          Imágenes de variante
                        </h2>

                        <p>{getVariantLabel(selectedVariant)}</p>
                      </div>

                      <button
                        type="button"
                        className={styles.CloseVariantImagesModalButton}
                        onClick={handleCloseVariantImages}
                        disabled={isSubmitting}
                        aria-label="Cerrar selector de imágenes"
                      >
                        ×
                      </button>
                    </header>

                    <div className={styles.VariantImagesModalContent}>
                      {values.images.length === 0 ? (
                        <div className={styles.EmptyConfiguration}>
                          Primero sube imágenes en la sección “Imágenes del
                          producto”.
                        </div>
                      ) : (
                        <div className={styles.VariantImagesSelectionGrid}>
                          {values.images.map((image) => {
                            const isSelected = normalizeStringArray(
                              selectedVariant.images
                            ).includes(image.localId);

                            return (
                              <button
                                key={image.localId}
                                type="button"
                                className={
                                  isSelected
                                    ? `${styles.VariantImageOption} ${styles.VariantImageOptionSelected}`
                                    : styles.VariantImageOption
                                }
                                onClick={() =>
                                  handleToggleVariantImage(
                                    selectedVariantImageIndex,
                                    image.localId
                                  )
                                }
                                disabled={isSubmitting}
                                aria-pressed={isSelected}
                              >
                                <img src={image.url} alt={image.name} />

                                <span
                                  className={
                                    styles.VariantImageSelectionMark
                                  }
                                >
                                  {isSelected ? "✓" : ""}
                                </span>

                                <span
                                  className={styles.VariantImageOptionName}
                                >
                                  {image.name}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <footer className={styles.VariantImagesModalFooter}>
                      <button
                        type="button"
                        className={styles.ConfirmColorButton}
                        onClick={handleCloseVariantImages}
                        disabled={isSubmitting}
                      >
                        Listo
                      </button>
                    </footer>
                  </div>
                </div>
              )}
            </>
          );
        }}
      </Formik>
    </section>
  );
}